import axios, { AxiosResponse } from 'axios';
import {
  User,
  UserListParams,
  UserListResponse,
  UserPermissionsRequest,
  PermissionGroup,
  PermissionGroupCreateRequest,
  PermissionGroupUpdateRequest,
  Program,
  SuccessResponse,
  EffectivePermission,
  UserPermissionMatrix,
  PermissionAnalysis,
  GroupMembers,
  GroupPermissions,
  ProgramTree
} from '../types/user';

// API 基礎設定
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_VERSION = '/api/v2';

// 建立 axios 實例 for V2 API
const apiClientV2 = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json'
  },
  timeout: 30000
});

// 請求攔截器 - 自動添加認證 token
apiClientV2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器 - 統一錯誤處理
apiClientV2.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 處理 403 權限不足錯誤
    if (error.response?.status === 403) {
      // 使用 antd 的 message 組件顯示錯誤訊息
      const message = (await import('antd')).message;
      message.error('權限不足，請聯繫管理員');
      return Promise.reject(error);
    }
    
    // 如果是 401 錯誤且沒有重試過
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // 嘗試刷新 token (使用 V1 API)
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const { authApi } = await import('./userApi');
          const response = await authApi.refreshToken({ refreshToken });
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          
          // 重試原始請求
          originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
          return apiClientV2(originalRequest);
        }
      } catch (refreshError) {
        // 刷新失敗，顯示錯誤訊息並重定向到登入頁
        const message = (await import('antd')).message;
        message.error('登入已過期，請重新登入');
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        sessionStorage.clear();
        
        // 延遲一秒後跳轉，讓使用者看到訊息
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        
        return Promise.reject(refreshError);
      }
    }
    
    // 如果是 401 但沒有 refreshToken，直接提示重新登入
    if (error.response?.status === 401) {
      const message = (await import('antd')).message;
      message.error('登入已過期，請重新登入');
      
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.clear();
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
    
    // 處理速率限制錯誤
    if (error.response?.status === 429) {
      error.message = '請求頻率過高，請稍後再試';
    }
    
    return Promise.reject(error);
  }
);

// 權限管理 V2 API
export const permissionApiV2 = {
  // === 權限群組管理 ===
  
  // 取得權限群組清單
  async getPermissionGroups(params?: { 
    page?: number; 
    limit?: number; 
    search?: string; 
    isActive?: boolean 
  }): Promise<SuccessResponse<{ items: PermissionGroup[]; pagination: any }>> {
    const response = await apiClientV2.get<SuccessResponse<{ items: PermissionGroup[]; pagination: any }>>(
      '/permission-groups', 
      { params }
    );
    return response.data;
  },

  // 取得單一權限群組
  async getPermissionGroup(groupId: string): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClientV2.get<SuccessResponse<PermissionGroup>>(
      `/permission-groups/${groupId}`
    );
    return response.data;
  },

  // 建立權限群組
  async createPermissionGroup(groupData: PermissionGroupCreateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClientV2.post<SuccessResponse<PermissionGroup>>(
      '/permission-groups', 
      groupData
    );
    return response.data;
  },

  // 更新權限群組
  async updatePermissionGroup(groupId: string, groupData: PermissionGroupUpdateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClientV2.put<SuccessResponse<PermissionGroup>>(
      `/permission-groups/${groupId}`, 
      groupData
    );
    return response.data;
  },

  // 刪除權限群組
  async deletePermissionGroup(groupId: string): Promise<SuccessResponse<void>> {
    const response = await apiClientV2.delete<SuccessResponse<void>>(
      `/permission-groups/${groupId}`
    );
    return response.data;
  },

  // === 群組成員管理 ===

  // 取得群組成員
  async getGroupMembers(groupId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<SuccessResponse<GroupMembers>> {
    const response = await apiClientV2.get<SuccessResponse<GroupMembers>>(
      `/permission-groups/${groupId}/members`,
      { params }
    );
    return response.data;
  },

  // 添加群組成員
  async addGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    const response = await apiClientV2.post<SuccessResponse<void>>(
      `/permission-groups/${groupId}/members`,
      { userIds }
    );
    return response.data;
  },

  // 移除群組成員
  async removeGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    const response = await apiClientV2.delete<SuccessResponse<void>>(
      `/permission-groups/${groupId}/members`,
      { data: { userIds } }
    );
    return response.data;
  },

  // === 群組權限管理 ===

  // 取得群組權限
  async getGroupPermissions(groupId: string): Promise<SuccessResponse<GroupPermissions>> {
    const response = await apiClientV2.get<SuccessResponse<GroupPermissions>>(
      `/permission-groups/${groupId}/permissions`
    );
    return response.data;
  },

  // 設定群組權限
  async setGroupPermissions(groupId: string, permissions: UserPermissionsRequest): Promise<SuccessResponse<void>> {
    const response = await apiClientV2.put<SuccessResponse<void>>(
      `/permission-groups/${groupId}/permissions`,
      permissions
    );
    return response.data;
  },

  // === 使用者權限管理 ===

  // 取得使用者權限
  async getUserPermissions(userId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>> {
    const response = await apiClientV2.get<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>>(
      `/users/${userId}/permissions`,
      { params }
    );
    return response.data;
  },

  // 設定使用者權限
  async setUserPermissions(userId: string, permissions: UserPermissionsRequest): Promise<SuccessResponse<void>> {
    const response = await apiClientV2.put<SuccessResponse<void>>(
      `/users/${userId}/permissions`,
      permissions
    );
    return response.data;
  },

  // 取得使用者有效權限
  async getUserEffectivePermissions(userId: string): Promise<SuccessResponse<{
    userId: string;
    userName: string;
    effectivePermissions: EffectivePermission[];
  }>> {
    const response = await apiClientV2.get<SuccessResponse<{
      userId: string;
      userName: string;
      effectivePermissions: EffectivePermission[];
    }>>(`/users/${userId}/effective-permissions`);
    return response.data;
  },

  // 取得當前使用者權限
  async getCurrentUserPermissions(): Promise<SuccessResponse<{
    userId: string;
    userName: string;
    permissions: any[];
  }>> {
    const response = await apiClientV2.get<SuccessResponse<{
      userId: string;
      userName: string;
      permissions: any[];
    }>>('/users/current/permissions');
    return response.data;
  },

  // 取得使用者權限矩陣
  async getUserPermissionMatrix(userId: string): Promise<SuccessResponse<UserPermissionMatrix>> {
    const response = await apiClientV2.get<SuccessResponse<UserPermissionMatrix>>(
      `/users/${userId}/permission-matrix`
    );
    return response.data;
  },

  // === 程式管理 ===

  // 取得程式清單
  async getPrograms(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<SuccessResponse<{ items: Program[]; pagination: any }>> {
    const response = await apiClientV2.get<SuccessResponse<{ items: Program[]; pagination: any }>>(
      '/programs',
      { params }
    );
    return response.data;
  },

  // 取得程式樹狀結構
  async getProgramTree(): Promise<SuccessResponse<ProgramTree[]>> {
    const response = await apiClientV2.get<SuccessResponse<ProgramTree[]>>('/programs/tree');
    return response.data;
  },

  // 取得單一程式
  async getProgram(programId: number): Promise<SuccessResponse<Program>> {
    const response = await apiClientV2.get<SuccessResponse<Program>>(`/programs/${programId}`);
    return response.data;
  },

  // === 權限檢查 ===

  // 檢查單一權限
  async checkPermission(userId: string, programId: number, permission: string): Promise<SuccessResponse<{
    hasPermission: boolean;
    userId: string;
    programId: number;
    permission: string;
  }>> {
    const response = await apiClientV2.get<SuccessResponse<{
      hasPermission: boolean;
      userId: string;
      programId: number;
      permission: string;
    }>>('/permissions/check', {
      params: { userId, programId, permission }
    });
    return response.data;
  },

  // 批量檢查權限
  async checkMultiplePermissions(checks: {
    userId: string;
    checks: Array<{
      programId: number;
      permission: string;
    }>;
  }): Promise<SuccessResponse<{
    results: Array<{
      programId: number;
      permission: string;
      hasPermission: boolean;
    }>;
  }>> {
    const response = await apiClientV2.post<SuccessResponse<{
      results: Array<{
        programId: number;
        permission: string;
        hasPermission: boolean;
      }>;
    }>>('/permissions/check-multiple', checks);
    return response.data;
  },

  // === 權限分析 ===

  // 取得使用者權限分析
  async getUserPermissionAnalysis(userId: string): Promise<SuccessResponse<PermissionAnalysis>> {
    const response = await apiClientV2.get<SuccessResponse<PermissionAnalysis>>(
      `/users/${userId}/permission-analysis`
    );
    return response.data;
  }
};

// 遷移輔助函數 - 幫助從 V1 遷移到 V2
export const migrationHelper = {
  // 轉換 V1 權限格式到 V2（保留 null = 未設定）
  convertV1PermissionsToV2(v1Permissions: any[]): any[] {
    return v1Permissions.map(perm => ({
      programId: perm.programId,
      addNew: perm.addNew ?? null,
      edit: perm.edit ?? null,
      delete: perm.delete ?? null,
      query: perm.query ?? null,
      export: perm.export ?? null,
      print: perm.print ?? null,
      approve: perm.approve ?? null
    }));
  },

  // 轉換 V2 權限格式到 V1（向後相容，保留 null = 未設定）
  convertV2PermissionsToV1(v2Permissions: any[]): any[] {
    return v2Permissions.map(perm => ({
      programId: perm.programId ?? perm.ProgramId,
      programName: perm.programName || perm.ProgramName || '',
      addNew: perm.addNew ?? perm.AddNew ?? null,
      edit: perm.edit ?? perm.Edit ?? null,
      delete: perm.delete ?? perm.Delete ?? perm.del ?? null,
      export: perm.export ?? perm.Export ?? null,
      print: perm.print ?? perm.Print ?? perm.printer ?? null,
      query: perm.query ?? perm.Query ?? null,
      approve: perm.approve ?? perm.Approve ?? null
    }));
  }
};

export default permissionApiV2;